import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { FormControlLabel, Checkbox, Snackbar } from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import { Alert } from "@material-ui/lab";

import uiConf from "../../config/uiConf";

import apiClient from "../../lib/common/apiClient";
import checkValidation from "../../lib/utils/checkValidation";

import Dialog from "../dialog/functional/Dialog";
import DialogHeader from "../dialog/functional/DialogHeader";
import DialogHeaderWithClose from "../dialog/functional/DialogHeaderWithClose";
import DialogTitle from "../dialog/functional/DialogTitle";
import DialogContent from "../dialog/functional/DialogContent";
import DialogActionsOkCancel from "../dialog/functional/DialogActionsOkCancel";
import AutoForm from "../form/functional/AutoForm";
import failureKey from "../../lib/common/failureKey";

const useStyles = makeStyles((theme) => ({
    keepOpenSwitch: {
        marginLeft: "8px"
    },
    label: {
        fontSize: "smaller",
        color: theme.palette.primary.text
    },
}));

function BizPlaceDialog(props) {

    const classes = useStyles();
    const { t } = useTranslation();

    const [instance, setInstance] = useState();
    const [interaction, setInteraction] = useState(false);
    const [formData, setFormData] = useState();
    const [notification, setNotification] = useState({
        open: false,
        severity: "success",
        msg: "",
    });
    const [openProgress, setOpenProgress] = useState(false);

    const [keepOpen, setKeepOpen] = useState(false);

    const [, setZipCodeData] = useState({});

    const {

        // Dialog Properties

        id,
        title,
        mode,
        platform,
        resizable,
        moveable,
        fullWidth,
        maxWidth,
        onInitialized,
        noKeepOpen,
        method,
        // Form Properties

        formId,
        schema,
        initialData,
        setInitialDataCallback,
        modifyFormDataCallback,
        checkShowCallback,
        checkDisabledCallback,
        checkFormValidationCallback,
        checkItemValidationCallback,
        modifyUrlCallback,

        // customize error message callback

        customizeErrorMsgCallback,

        // AutoFormDialog Properties

        onClose,
        onChange,
        onSuccess,
        onFailure,

        // Do Not Notify Using Snackbar by Dialog itself

        doNotNotify,
        doNotNotifyFailure,
        doNotNotifySuccess,

        doNotUseKeyAsUrlWhenUpdate,

        barcodeCallback,
        focusRefs,

        buttonLabel,

        ...others

    } = props;

    useEffect(()=>{
        setFormData({...initialData});
    },[initialData]);

    const notifyWarning = (msg) => {

        console.warn(msg);

        if (!(doNotNotify || doNotNotifyFailure)) {

            setNotification({
                severity: "warning",
                msg: msg,
                open: true,
            });

        }

    };

    const checkItemValidataion = (formData, schema) => {

        for (const column of schema.columns) {

            let [valid, msg] = checkValidation(mode, column, column.validation, formData[column.name] || "");

            if (valid && checkItemValidationCallback) {

                [valid, msg] = checkItemValidationCallback(formData, column);

            }

            if (!valid) {

                notifyWarning(msg);
                return false;

            }

        }

        return true;

    }

    const notifyFailure = (msg) => {
        
        if (!(doNotNotify || doNotNotifyFailure)) {

            setNotification({
                severity: "error",
                msg: msg,
                open: true,
            });

        }

    };

    const notifySuccess = (msg) => {

        if (doNotNotifySuccess === false) {

            setNotification({
                severity: "success",
                msg: msg,
                open: true,
            });

        }

    };

    const onCloseDialog = () => {

        setInteraction(false);
        setFormData({})
        if (onClose) {

            onClose();

        }

    }

    const onCloseNotification = () => {

        setNotification((notification) => {

            return ({ ...notification, open: false });

        });

    };

    const onDialogInitialized = (instance) => {

        setInstance(instance);

        if (onInitialized) {

            onInitialized(instance);

        }

    };

    const onChangeFormData = (formData) => {
        setInteraction(true);

        setFormData(prev => {
            return {
                ...prev,
                ...formData,
            };
        });
        
    };

    const handleAddressSelectCallback = (zipData) => {

          setInteraction(true);
      
          setZipCodeData({ ...zipData });
          setFormData((prevFormData) => ({
            ...prevFormData,
            zipcode: zipData?.zonecode ?? '',
            englishAddress: zipData?.addressEnglish ?? '',
            address: zipData?.address ?? '',
          }));
      };
      
    const onApiSuccess = (result, schema) => {

        setOpenProgress(false);

        switch (method) {

            case "put":

                notifySuccess(t("success.updateSuccess", { updateCount: 1 }));
                break;

            default:

                notifySuccess(t("success.insertSuccess", { table: schema.name }));
                break;

        }

        setKeepOpen((keepOpen) => {

            if (!keepOpen) {

                instance.hide();

            }

            return keepOpen;

        });

        if (onSuccess) {

            setTimeout(onSuccess(result.data), 0);

        }

    };

    const onApiFailure = (reason) => {
        setOpenProgress(false);
        let msg;

        if (customizeErrorMsgCallback) {

            msg = customizeErrorMsgCallback ( reason );

        } else {

            switch (method) {

                case "put":
    
                    msg = t ( failureKey ( "update", reason.response?.status ), { reason: reason.response ? reason.response.data : JSON.stringify(reason) } );
                    break;
    
                default:
    
                    msg = t ( failureKey ( "insert", reason.response?.status ), { reason: reason.response ? reason.response.data : JSON.stringify(reason) } );
                    break;
    
            }
        }

        if (onFailure) {
            onFailure(reason, notifyFailure);

        } else {

            notifyFailure ( msg );

        }

    };

     const onOk = (schema, formData) => {

        if (!checkItemValidataion(formData, schema)) {

            console.error("Form Validation Failed");
            return;

        }

        let url = "/api/" + schema.name;
    
        let apiData = { ...formData };

        let form = new FormData();
        let json = {};
        let deleted = {};

        if (schema) { 
            let columnMap = {};
            schema.columns.map(column => columnMap[column.name] = column);
            schema.columnMap = columnMap;
        }
        
        for(const formItem in apiData) {

            if (schema.columnMap && schema.columnMap[formItem] && schema.columnMap[formItem].formItemType === "file") {

                deleted[formItem] = apiData[formItem].deletedFiles;

                for (const file of apiData[formItem].activeFiles) {

                    if (file.type === "url") continue;
                    
                        form.append(formItem, file.file);

                }

            } else {

                if (apiData[formItem] !== undefined) {

                    json[formItem] = apiData[formItem];

                }

            }

        }
        form.append("deleted", JSON.stringify(deleted));
        form.append("json", JSON.stringify(json));
        setOpenProgress(true);

        switch (method?.toLowerCase()) {
            case "put":
                apiClient
                    .put(`${url}/${formData.bizCode}`, form, {
                        headers: {
                            "Content-Type": "multipart/form-data",
                        },
                    })
                    .then((result) => onApiSuccess(result,schema))
                    .catch((reason) => onApiFailure(reason));
                break;
            case "patch":
                apiClient
                    .patch(url, form, {
                        headers: {
                            "Content-Type": "multipart/form-data",
                        },
                    })
                    .then((result) => onApiSuccess(result,schema))
                    .catch((reason) => onApiFailure(reason));
                break;
            default:
                apiClient
                    .post(url, form, {
                        headers: {
                            "Content-Type": "multipart/form-data",
                        },
                    })
                    .then((result) => onApiSuccess(result,schema))
                    .catch((reason) => onApiFailure(reason));
                break;
        }
    };

    


    const onCancel = () => {

        instance.hide();

    };

    const checkKeepOpen = () => {

        return (
            <>

                <FormControlLabel
                    control={

                        <Checkbox
                            className={classes.keepOpenSwitch}
                            checked={keepOpen}
                            color="primary"
                            onChange={() => setKeepOpen((keepOpen) => (!keepOpen))}
                            name="checkKeepOpen"
                            size="small"
                        />
                    }
                    label={<span className={classes.label}>{t("term.keepDialogOpen")}</span>}
                />
                <div style={{ flexGrow: 1 }}></div>

            </>

        )
    }

    return (
        <Dialog
            id={id}
            title={title}
            platform={platform ? platform : uiConf.dialogPlatform}
            moveable={moveable ? moveable : uiConf.dialogMoveable}
            resizable={resizable ? resizable : uiConf.dialogResizable}
            fullWidth={fullWidth ? fullWidth : uiConf.dialogFullWidth}
            maxWidth={maxWidth ? maxWidth : uiConf.autoFormDialogMaxWidth}
            onClose={onCloseDialog}
            onInitialized={onDialogInitialized}
            {...others}
        >
            {uiConf.modalWithCloseButton ? (
                <DialogHeaderWithClose>
                    <DialogTitle>
                        {title}
                    </DialogTitle>
                </DialogHeaderWithClose>
            ) : (
                <DialogHeader>
                    <DialogTitle>
                        {title}
                    </DialogTitle>
                </DialogHeader>
            )}

            <DialogContent dividers>
                <AutoForm
                    id={formId}
                    schema={schema}
                    mode="edit"
                    initialData={formData}
                    setInitialDataCallback={setInitialDataCallback}
                    checkShowCallback={checkShowCallback}
                    checkDisabledCallback={checkDisabledCallback}
                    checkValidationCallback={checkItemValidationCallback}
                    onChange={onChangeFormData}
                    barcodeCallback={barcodeCallback}
                    onAddressSelectCallback={handleAddressSelectCallback}
                    focusRefs={focusRefs}
                    modifyFormDataCallback={(autoFormData, name, value) => {
                        if (['zipcode', 'address', 'englishAddress'].includes(name)) {
                            autoFormData.zipcode = formData.zipcode
                            autoFormData.address = formData.address
                            autoFormData.englishAddress = formData.englishAddress
                        }
                    }}
                />

                <Snackbar
                    open={notification.open && notification.severity !== "success"}
                    autoHideDuration={uiConf.dialogNotifyAutoHideDuration}
                    onClose={onCloseNotification}
                >
                    <Alert
                        onClose={onCloseNotification}
                        variant="filled"
                        severity={notification.severity}
                    >
                        {notification.msg}
                    </Alert>
                </Snackbar>
            </DialogContent>

            <DialogActionsOkCancel
                firstActionComponent={noKeepOpen ? () => <></> : checkKeepOpen}
                labels={buttonLabel ? buttonLabel : [mode === "edit" ? t("common.edit") : t("common.create")]}
                buttonDisabled={[!interaction, false]}
                onOk={() => onOk(schema, formData)}
                onCancel={onCancel}
                openProgress={openProgress}
            />
        </Dialog>
    );
}

export default BizPlaceDialog;
